import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import { useAuth } from '../composables/useAuth'
import { useErrorStore } from '../stores/errorStore'

// -------------------
// Declare meta typing
// -------------------
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    scope?: string
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'reference-design' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'reference-design' },
    children: [
      // {
      //   name: 'dashboard',
      //   path: 'dashboard',
      //   component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      // },
      {
        name: 'background',
        path: 'background',
        component: () => import('../pages/admin/dashboard/BackgroundDashboard.vue'),
      },
      {
        name: 'devops',
        path: 'devops',
        meta: { scope: import.meta.env.VITE_SCOPE_ADMIN },
        children: [
          {
            name: 'solution-template-audit',
            path: 'audit',
            component: () => import('../pages/solution-templates/AuditLogPage.vue'),
            meta: { breadcrumb: 'Audit' },
          },
          {
            name: 'export',
            path: 'export',
            component: () => import('../pages/reference-designs/ExportPage.vue'),
          },
          {
            name: 'import',
            path: 'import',
            component: () => import('../pages/reference-designs/ImportPage.vue'),
          },
          {
            name: 'upgrade-schema',
            path: 'upgrade-schema',
            component: () => import('../pages/devops/DevOpsPage.vue'),
          },
          {
            name: 'upgrade-index',
            path: 'upgrade-index',
            redirect: { name: 'upgrade-index-reference-design' },
            meta: { breadcrumb: 'Upgrade Index' },
            children: [
              {
                name: 'upgrade-index-reference-design',
                path: 'reference-design',
                component: () => import('../pages/devops/UpgradeIndexPage.vue'),
                meta: { breadcrumb: 'Reference Design', parent: 'upgrade-index' },
              },
              {
                name: 'upgrade-index-audit',
                path: 'audit',
                component: () => import('../pages/devops/AuditUpgradeIndexPage.vue'),
                meta: { breadcrumb: 'Audit', parent: 'upgrade-index' },
              },
            ],
          },
        ],
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'reference-design',
        path: 'reference-design',
        component: () => import('../pages/reference-designs/RefDesignsPage.vue'),
        meta: { scope: import.meta.env.VITE_SCOPE_ADMIN, breadcrumb: 'Reference Design' },
      },
      {
        name: 'resources',
        path: 'reference-design/resources',
        component: () => import('../pages/reference-designs/ResourcesPage.vue'),
        meta: {
          scope: import.meta.env.VITE_SCOPE_ADMIN,
          breadcrumb: 'Resources',
          parent: 'reference-design',
        },
      },
      {
        name: 'solution-templates',
        path: 'solution-templates',
        redirect: { name: 'templates' },
        meta: {
          scope: import.meta.env.VITE_SCOPE_ADMIN,
          breadcrumb: 'Solution Templates',
        },
        children: [
          {
            name: 'applications',
            path: 'applications',
            component: () => import('../pages/applications/ApplicationsPage.vue'),
            meta: {
              scope: import.meta.env.VITE_SCOPE_ADMIN,
              breadcrumb: 'Applications',
              parent: 'solution-templates',
            },
          },
          {
            name: 'templates',
            path: 'templates',
            component: () => import('../pages/solution-templates/SolutionTemplatesPage.vue'),
            meta: {
              scope: import.meta.env.VITE_SCOPE_ADMIN,
              breadcrumb: 'Templates',
              parent: 'solution-templates',
            },
          },
        ],
      },
      {
        name: 'application-detail',
        path: '/solution-templates/applications/:id',
        component: () => import('../pages/applications/ApplicationDetail.vue'),
        meta: {
          parent: 'applications',
          breadcrumb: 'Detail',
        },
      },
      {
        name: 'solution-template-detail',
        path: '/solution-templates/templates/:id',
        component: () => import('../pages/solution-templates/SolutionTemplateDetail.vue'),
        meta: {
          parent: 'templates',
          breadcrumb: 'Detail',
        },
      },
      {
        name: 'evaluation-kits',
        path: 'evaluation-kits',
        component: () => import('../pages/evaluation-kits/EvaluationKitsPage.vue'),
        meta: { scope: import.meta.env.VITE_SCOPE_ADMIN },
      },
      {
        name: 'evaluation-kit-detail',
        path: '/evaluation-kits/:id',
        component: () => import('../pages/evaluation-kits/EvaluationKitDetail.vue'),
        meta: {
          parent: 'evaluation-kits',
          breadcrumb: 'Detail',
        },
      },
      {
        name: 'software-projects',
        path: 'software-projects',
        component: () => import('../pages/software-projects/SoftwareProjectsPage.vue'),
        meta: { scope: import.meta.env.VITE_SCOPE_ADMIN, breadcrumb: 'Software Projects' },
      },
      {
        name: 'toolchains',
        path: 'software-projects/toolchains',
        component: () => import('../pages/toolchains/ToolchainsPage.vue'),
        meta: {
          scope: import.meta.env.VITE_SCOPE_ADMIN,
          breadcrumb: 'Toolchains',
          parent: 'software-projects',
        },
      },
      {
        name: 'toolchain-detail',
        path: '/software-projects/toolchains/:id',
        component: () => import('../pages/toolchains/ToolchainDetail.vue'),
        meta: {
          parent: 'toolchains',
          breadcrumb: 'Detail',
        },
      },
      {
        name: 'software-project-detail',
        path: '/software-projects/:id',
        component: () => import('../pages/software-projects/SoftwareProjectDetail.vue'),
        meta: {
          parent: 'software-projects',
          breadcrumb: 'Detail',
        },
      },
      {
        name: 'reference-design-detail',
        path: '/reference-design/:id',
        component: () => import('../pages/reference-designs/RefDesignDetail.vue'),
        meta: {
          parent: 'reference-design',
          breadcrumb: 'Detail',
        },
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
        meta: { requiresAuth: false },
      },
      {
        name: 'callback',
        path: 'callback',
        component: () => import('../pages/auth/Callback.vue'),
        meta: { requiresAuth: false },
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '403',
    path: '/403',
    component: () => import('../pages/403.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE_PATH),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const auth = useAuth()
  const errorStore = useErrorStore()
  const isCallbackPage = to.name === 'callback'

  if (isCallbackPage) {
    const code = to.query.code as string | undefined
    const state = to.query.state as string | undefined

    if (!code || !state) {
      return next({ name: 'login' })
    }

    try {
      const result = await auth.authCallback({ code, state })
      if (result.isSuccess) {
        next({ name: 'reference-design' })
      } else {
        next({ name: 'login' })
      }
    } catch (error) {
      let message = 'An unexpected error occurred.'
      if (error instanceof Error) {
        message = error.message
      } else if (typeof error === 'string') {
        message = error
      } else {
        message = JSON.stringify(error)
      }
      errorStore.setErrors([message])
      next({ name: 'login' })
      console.error(error)
    }
  } else {
    await auth.setAuth()
    const isLoginPage = to.name === 'login'
    if (isLoginPage && auth.isAuthenticated.value) {
      return next({ name: 'reference-design' })
    }

    if (auth.error.value && !isLoginPage) {
      errorStore.setErrors([auth.error.value])
    }

    const requiresAuth = to.meta.requiresAuth !== false
    if (requiresAuth) {
      if (!auth.isAuthenticated.value) {
        return next({ name: 'login' })
      }

      // if (to.meta.scope && !auth.hasPermission(to.meta.scope)) {
      //   return next({ name: '403' })
      // }
    }
  }

  next()
})

export default router
